import { createPersistedSliceStorage, type PersistedSliceConfig } from "@mgdx/shared/src/utils/persistedSlice";
import { persist } from "zustand/middleware";
import { createStore } from "zustand/vanilla";

export type VideoCallingState = {
  isOpenSubWindow: boolean;
  currentCounselingId: string;
  /** 要指導の通話で表示する OTC 受付の ID。通常の予約の通話では空 */
  currentOtcScreeningId: string;
  isCheckedVideoCalling: boolean;
  isMoveSubWindow: boolean;
};

export const initialVideoCallingState: VideoCallingState = {
  isOpenSubWindow: false,
  currentCounselingId: "",
  currentOtcScreeningId: "",
  isCheckedVideoCalling: false,
  isMoveSubWindow: false,
};

export type VideoCallingActions = {
  openVideoCalling: (counselingId: string, otcScreeningId?: string) => void;
  closeVideoCalling: () => void;
  setIsCheckedVideoCalling: (isCheckedVideoCalling: boolean) => void;
  moveCallingSubWindow: () => void;
  clearVideoCallingState: () => void;
};

export type VideoCallingStore = VideoCallingState & { actions: VideoCallingActions };

const videoCallingPersistConfig: PersistedSliceConfig<VideoCallingState> = {
  key: "persist:videoCalling",
  legacyReduxPersistVersion: -1,
  fields: [
    "isOpenSubWindow",
    "currentCounselingId",
    "currentOtcScreeningId",
    "isCheckedVideoCalling",
    "isMoveSubWindow",
  ],
};

export const createVideoCallingStore = ({ persistent = false }: { persistent?: boolean } = {}) =>
  createStore<VideoCallingStore>()(
    persist(
      (set) => ({
        ...initialVideoCallingState,
        actions: {
          // 別の通話の OTC 受付 ID が残らないよう、予約 ID と同時に差し替える
          openVideoCalling: (counselingId, otcScreeningId) =>
            set({
              currentCounselingId: counselingId,
              currentOtcScreeningId: otcScreeningId ?? "",
              isOpenSubWindow: true,
            }),
          closeVideoCalling: () =>
            set({ currentCounselingId: "", currentOtcScreeningId: "", isOpenSubWindow: false, isMoveSubWindow: false }),
          setIsCheckedVideoCalling: (isCheckedVideoCalling) => set({ isCheckedVideoCalling }),
          moveCallingSubWindow: () => set({ isMoveSubWindow: true }),
          clearVideoCallingState: () => set({ ...initialVideoCallingState }),
        },
      }),
      {
        name: "persist:videoCalling",
        skipHydration: true,
        storage: createPersistedSliceStorage(
          persistent ? videoCallingPersistConfig : undefined,
          initialVideoCallingState
        ),
        partialize: ({ actions: _actions, ...state }) => state,
        merge: (persisted, current) => (persistent ? { ...current, ...(persisted as VideoCallingState) } : current),
      }
    )
  );

export type VideoCallingStoreApi = ReturnType<typeof createVideoCallingStore>;
